import { Button } from "@homebound/beam";
import { useNavigate } from "react-router-dom";
import { StepActions, useStepperContext } from "src/components/stepper";
import { GuidedChangeEventStep } from "src/routes/projects/change-events/guided/utils";
import { WorkflowActions } from "src/routes/projects/change-events/guided/workflows/types";
import { createChangeEventsUrl } from "src/RouteUrls";

type GuidedChangeEventStepActionsProps = {
  projectId: string;
  actions: WorkflowActions;
  // Whether the current step is complete enough to move on.
  canAdvance: boolean;
  // Whether the final submit is allowed (the whole workflow form is valid).
  canSubmit: boolean;
};

/**
 * Actions for the guided change event wizard, rendered into the stepper's bottom bar via `StepActions`.
 * Generic across workflows: Next/Submit behavior comes from the active workflow's `actions`.
 */
export function GuidedChangeEventStepActions(props: GuidedChangeEventStepActionsProps) {
  const { projectId, actions, canAdvance, canSubmit } = props;
  const { prevStep, nextStep, isFirstStep, isLastStep, currentStep } = useStepperContext();
  const navigate = useNavigate();

  async function handleNext() {
    // Run the workflow's per-step side effect, if any, before advancing.
    const onNext = actions.onNext?.[currentStep.value as GuidedChangeEventStep];
    const result = await onNext?.();
    if (result !== false) nextStep();
  }

  return (
    <StepActions>
      <>
        {!isFirstStep && <Button variant="tertiary" label="Back" size="lg" onClick={prevStep} />}
        <Button
          variant="secondary"
          label="Cancel"
          size="lg"
          onClick={() => navigate(createChangeEventsUrl(projectId))}
        />
        {isLastStep ? (
          <Button
            label={actions.submitLabel ?? "Create Change Event"}
            size="lg"
            disabled={!canSubmit || actions.submitting}
            onClick={actions.onSubmit}
          />
        ) : (
          <Button label="Next" size="lg" disabled={!canAdvance} onClick={handleNext} />
        )}
      </>
    </StepActions>
  );
}
